import { useCallback } from 'react';

import {
  type AiBuilderChatState,
  type AiBuilderResponseFeedback,
} from '../stores/types';
import {
  type AiBuilderResponseFeedbackTargetMessage,
  isAiBuilderResponseFeedbackTarget,
  updateAiBuilderResponseFeedbackState,
} from '../utils/aiBuilderResponseFeedback';
import { updateResponseFeedback } from '../utils/serverAiBuilderChatTransport';

export const useAiBuilderResponseFeedback = ({
  draftInstanceId,
  getChatState,
  updateState,
}: {
  draftInstanceId: string;
  getChatState: () => AiBuilderChatState;
  updateState: (
    updater: (previous: AiBuilderChatState) => AiBuilderChatState,
  ) => void;
}) =>
  useCallback(
    (messageId: string, feedback: AiBuilderResponseFeedback) => {
      const { messages, sessionId } = getChatState();
      const message = messages.find(
        (candidate): candidate is AiBuilderResponseFeedbackTargetMessage =>
          candidate.id === messageId &&
          isAiBuilderResponseFeedbackTarget(candidate),
      );
      if (!message?.traceId || !sessionId || message.feedbackPending) return;

      const previousFeedback = message.feedback ?? null;
      updateState(previous => ({
        ...previous,
        messages: updateAiBuilderResponseFeedbackState(
          previous.messages,
          messageId,
          { feedback, pending: true },
        ),
      }));

      void updateResponseFeedback({
        sessionId,
        draftInstanceId,
        traceId: message.traceId,
        feedback,
      })
        .then(() => {
          updateState(previous => ({
            ...previous,
            messages: updateAiBuilderResponseFeedbackState(
              previous.messages,
              messageId,
              { feedback, pending: false },
            ),
          }));
        })
        .catch(() => {
          updateState(previous => ({
            ...previous,
            messages: updateAiBuilderResponseFeedbackState(
              previous.messages,
              messageId,
              { feedback: previousFeedback, pending: false },
            ),
          }));
        });
    },
    [draftInstanceId, getChatState, updateState],
  );
